import { SignatoryRecord } from "../../presentation/components/pages/student-history";
import { ZoneShape } from "../../presentation/components/pages/ui-utils/zoneFilterTypes";
import SigningDocument from "./_SigningDocument";
import { ArchiveDocument, ArchiveFile } from "./ArchiveDocument";
import Campaign from "./Campaign";
import Chart from "./Chart";
import { PostComment } from "./Chat";
import { AppConfigs, ClassroomSizeConfig, FeederPatternConfig, SchoolDirectoryEntry, SeatLimitConfig, SiteImage } from "./Configs";
import Document from "./Document";
import DocumentTemplate from "./DocumentTemplate";
import Message from "./Message";
import Organization from "./Organization";
import Package from "./Package";
import Payment from "./Payment";
import Permission from "./Permission";
import PermissionType from "./PermissionType";
import Role from "./Role";
import SectionsData from "./Section";
import SelectRouting from "./SelectRouting";
import Signatory from "./Signatory";
import SignatoryGroup from "./SignatoryGroup";
import Student from "./Student";
import StudentPostgis, { PostgisAssignment, PostgisProgram, PostgisSchool, StudentsByDate, StudentsBySchoolDate } from "./StudentPostgis";
import User from "./User";

export enum OrganizationStatus {
  ACTIVE,
  UPDATED,
  INACTIVE,
  DELETED,
}

export enum StateStatus {
  INIT = "initial",
  LOADING = "loading",
  COMPLETE = "complete",
  ERROR = "error",
}

export type AdministratorState = {
  organizationsLoadStatus: StateStatus;
  organizations: Organization[];
  organizationsLoadError?: null;
  organizationSave: StateStatus;
  organizationSaveError?: null;
  organizationIdStatus?: StateStatus;
  organizationLoadStatus: StateStatus;
  organization?: Organization | null;
  organizationLoadError?: null;
  organizationDeleteError?: null;
  organizationDeleteStatus?: StateStatus;
  permissionsLoadStatus: StateStatus;
  permissions: Permission[];
  permissionsLoadError?: null;
  permissionTypesLoadStatus: StateStatus;
  permissionTypes: PermissionType[];
  permissionTypesLoadError?: null;
  permissionSaveStatus: StateStatus;
  permissionSaveError?: null;
  rolesLoadStatus: StateStatus;
  roles: Role[];
  rolesLoadError?: null;
  usersLoadStatus: StateStatus;
  user: User | null;
  userLoadStatus: StateStatus;
  userLoadError?: null;
  users: User[];
  usersLoadError?: null;
  userSaved: null;
  userSaveStatus: StateStatus;
  userSaveError?: Error | null;
  userCognitoSaveStatus: StateStatus;
  userCognitoSaveError?: null;
  userCognitoCheckStatus: StateStatus;
  userCognitoCheckStatusError?: null; 
  userCognitoExists: boolean;
  userDeleteStatus: StateStatus;
  userDeleteError?: null;
  userRegisterStatus: StateStatus;
  userRegisterError?: null;
  siteConfigs?: AppConfigs;
  siteConfigsLoad?: StateStatus;
  siteConfigsSave?: StateStatus;
  siteConfigsError?: null;
  fatherSiteConfigs?: AppConfigs;
  fatherSiteConfigsLoad?: StateStatus;
  fatherSiteConfigsError?: null;
  siteImages?: SiteImage | null;
  userOrgs?: User[] | null;
  userOrgsLoadStatus: StateStatus;
  userOrgsLoadError?: null;
  shapefilesLoadStatus: StateStatus;
  shapefiles: ZoneShape[];
  shapefilesLoadError?: null;
  shapefileSaveStatus: StateStatus;
  shapefileSaveError?: null;
  shapefileDeleteStatus: StateStatus;
  shapefileDeleteError?: null;
  seatLimitConfigsLoadStatus: StateStatus;
  seatLimitConfigs: SeatLimitConfig[];
  seatLimitConfigsLoadError?: null;
  seatLimitConfigSaveStatus: StateStatus;
  seatLimitConfigSaveError?: null;
  classroomSizeConfigsLoadStatus: StateStatus;
  classroomSizeConfigs: ClassroomSizeConfig[];
  classroomSizeConfigsLoadError?: null;
  classroomSizeConfigSaveStatus: StateStatus;
  classroomSizeConfigSaveError?: null;
  feederPatternConfigsLoadStatus: StateStatus;
  feederPatternConfigs: FeederPatternConfig[];
  feederPatternConfigsLoadError?: null;
  feederPatternConfigSaveStatus: StateStatus;
  feederPatternConfigSaveError?: null;
  feederPatternConfigDeleteStatus: StateStatus;
  feederPatternConfigDeleteError?: null;
  feederPatternConfigImportStatus: StateStatus;
  feederPatternConfigImportError?: null;
  schoolDirectoryLoadStatus: StateStatus;
  schoolDirectory: SchoolDirectoryEntry[];
  schoolDirectoryLoadError?: null;
  schoolDirectorySaveStatus: StateStatus;
  schoolDirectorySaveError?: null;
  schoolDirectoryBatchSaveStatus: StateStatus;
  schoolDirectoryBatchSaveError?: null;
  schoolDirectoryDeleteStatus: StateStatus;
  schoolDirectoryDeleteError?: null;
  programCodesLoadStatus: StateStatus;
  programCodes: Record<string, string>;
  programCodesLoadError?: null;
  programCodesSaveStatus: StateStatus;
  programCodesSaveError?: null;
};

export type RequestorState = {
  campaign: Campaign | null;
  campaignLoadStatus: StateStatus;
  campaignLoadError: any;
  campaignSaveStatus: StateStatus;
  campaignSaveError: any;
  campaignDeleteStatus: StateStatus;
  campaignDeleteError: any;
  campaignSendStatus: StateStatus;
  campaignSendError: any;
  campaignSaveSendStatus: StateStatus;
  campaignSaveSendError: any;
  campaignsLoadStatus: StateStatus;
  campaigns: Campaign[];
  campaignsLoadError: any;
  campaignsCountLoadStatus: StateStatus;
  campaignsCount: number | null;
  campaignsCountLoadError: any;
  documentStaffProcessedLoadStatus: StateStatus;
  documentStaffProcessed: Document | null;
  documentStaffProcessedLoadError: any;
  documentTemplateSaveStatus: StateStatus;
  documentTemplateSaveError: any;
  documentTemplate: DocumentTemplate | null;
  documentTemplateLoadStatus: StateStatus;
  documentTemplateLoadError: any;
  documentTemplateDeleteStatus: StateStatus;
  documentTemplateDeleteError: any;
  documentTemplateSignaturePositionStatus: StateStatus;
  documentTemplateSignaturePositionError: any;
  documentTemplatesLoadStatus: StateStatus;
  documentTemplates: DocumentTemplate[];
  documentTemplatesLoadError: null;
  packagesLoadStatus: StateStatus;
  packages: Package[];
  packagesLoadError: any;
  packageSaveStatus: StateStatus;
  packageSaveError: any;
  packageDeleteStatus: StateStatus;
  packageDeleteError: any;
  selectRoutingsLoadStatus: StateStatus;
  selectRoutings: SelectRouting[];
  selectRoutingsLoadError: any;
  selectRoutingSaveStatus: StateStatus;
  selectRoutingSaveError: any;
  selectRoutingDeleteStatus: StateStatus;
  selectRoutingDeleteError: any;
  signatoryGroupLoadStatus: StateStatus;
  signatoryGroups: SignatoryGroup[];
  signatoryGroupLoadError: any;
  signatoryGroupSaveStatus: StateStatus;
  signatoryGroupSaveError: any;
  signatoryGroupDeleteStatus: StateStatus;
  signatoryGroupDeleteError: any;
  message: Message | null;
  messageLoadStatus: StateStatus;
  messageLoadError: any;
  messageSaveStatus: StateStatus;
  messageSaveError: any;
  messageDeleteStatus: StateStatus;
  messageDeleteError: any;
  messagesLoadStatus: StateStatus;
  messages: Message[];
  messagesLoadError: any;
  messagesCountLoadStatus: StateStatus;
  messagesCount: number | null;
  messagesCountLoadError: any;
  messageSendStatus: StateStatus;
  messageSendError: any;
  externalSchoolIntegrationSectionsLoadStatus: StateStatus;
  externalSchoolIntegrationSections: SectionsData[];
  externalSchoolIntegrationSectionsLoadError: any;
  externalSchoolIntegrationGradesLoadStatus: StateStatus;
  externalSchoolIntegrationGrades: string[] | null;
  externalSchoolIntegrationGradesLoadError: any;
  externalSchoolIntegrationStudentsLoadStatus: StateStatus;
  externalSchoolIntegrationStudents: Student[];
  externalSchoolIntegrationStudentsLoadError: any;
  externalSchoolIntegrationStudentSignatoriesLoadStatus: StateStatus;
  externalSchoolIntegrationStudentSignatories: Signatory[] | null;
  externalSchoolIntegrationStudentSignatoriesError: any;
  externalSchoolIntegrationSignatoriesLoadStatus: StateStatus;
  externalSchoolIntegrationSignatories: Signatory[] | null;
  externalSchoolIntegrationSignatoriesError: any;
  signingDocuments: SigningDocument[];
  signingDocumentsLoadError: any;
  signingDocumentsLoadStatus: StateStatus;
  emailTracking: any;
  emailTrackingStats: any;
  emailTrackingLoadError: any;
  emailTrackingLoadStatus: StateStatus;
  chartDataStatus?: StateStatus;
  chartData?: Chart;
  chartDataError?: any;
  postgisSchoolsLoadStatus: StateStatus;
  postgisSchools: PostgisSchool[];
  postgisSchoolsLoadError: any;
  postgisGradesLoadStatus: StateStatus;
  postgisGrades: string[];
  postgisGradesLoadError: any;
  postgisProgramsLoadStatus: StateStatus;
  postgisPrograms: PostgisProgram[];
  postgisProgramsLoadError: any;
  postgisAssignmentsLoadStatus: StateStatus;
  postgisAssignments: PostgisAssignment[];
  postgisAssignmentsLoadError: any;
  postgisStudentsLoadStatus: StateStatus;
  postgisStudents: StudentPostgis[];
  postgisStudentsLoadError: any;
  postgisStudentsExcludedCount: number;
  studentsForGradesAndProgramsLoadStatus: StateStatus;
  studentsForGradesAndPrograms: StudentsByDate;
  studentsForGradesAndProgramsLoadError: any;
  studentsBySchoolLoadStatus: StateStatus;
  studentsBySchool: StudentsBySchoolDate;
  studentsBySchoolLoadError: any;
  programDemandLoadStatus: StateStatus;
  programDemand: any;
  programDemandLoadError: any;
  choiceDashboardLoadStatus: StateStatus;
  choiceDashboard: any;
  choiceDashboardLoadError: any;
  /** Id of the latest dashboard request; older responses are ignored. */
  choiceDashboardRequestId: number;
  projectedEnrollmentLoadStatus: StateStatus;
  projectedEnrollment: StudentsBySchoolDate;
  projectedEnrollmentLoadError: any;
  lastEvaluatedKey: any | null | undefined;
};

export type SessionState = {
  loggedUser?: User | null;
  loggedUserArray?: User[] | null;
  loginStatus: StateStatus;
  loginError?: string | null;
  menuVisible: true;
  menuSelected: [any];
  passwordUpdateStatus: StateStatus;
  passwordUpdate?: boolean;
  passwordUpdateError?: null;
  requestPasswordUpdateStatus: StateStatus;
  requestPasswordUpdate?: boolean;
  requestPasswordUpdateError?: null;
  currentPDFInstance?: null;
  currentPDFInstanceStatus: StateStatus;
  pinSendStatus: StateStatus;
  pinSendError?: null;
  cognitoAuthLoginStatus: StateStatus;
  cognitoAuthLoginError?: null | string;
  mfaSession?: string;
  mfaUsername?: string;
};

export type SignatoryState = {
  documentsLoadStatus: StateStatus;
  documents: Document[] | null;
  documentsLoadError: null;
  documentLoadStatus: StateStatus;
  document: Document | null;
  documentLoadError: null;
  documentSaveStatus: StateStatus;
  documentSaveError: null;
  signedDocument: null;
  documentSignStatus: StateStatus;
  documentSignError: null;
  documentSendStatus: StateStatus;
  documentSendError: null;
  documentSendBackStatus: StateStatus;
  documentSendBackError: null;
  documentSaveProgressStatus: StateStatus;
  documentSaveProgressError: null;
  signatoryLoadStatus: StateStatus;
  signatory: Signatory | null;
  signatoryLoadError: null | string;
  signatorySaveStatus: StateStatus;
  signatorySaveError: null;
  pdfTranslateStatus: StateStatus;
  pdfTranslated: null;
  pdfTranslateError: null;
  chatSaveStatus: StateStatus;
  chat: null;
  chatSaveError: null;
  chatDeleteStatus: StateStatus;
  chatDeleteError: null;
  chatsLoadStatus: StateStatus;
  chats: PostComment[] | null;
  chatsLoadError: null;
  collectiveSelectedSignatory: Signatory | undefined;
  collectiveDocuments: Document[] | null;
  collectiveMessages: Message[] | null;
  collectiveData: SignatoryRecord[] | null;
  paymentSaveStatus: StateStatus;
  paymentSaveError: null;
  paymentLoadStatus: StateStatus;
  payment: Payment | null;
  paymentLoadError: null;
  paymentsLoadStatus: StateStatus;
  payments: Payment[] | null;
  paymentsLoadError: null;
  paymentsCountLoadStatus: StateStatus;
  paymentsCount: number | null;
  paymentsCountLoadError: null;
  pdfESignLoadStatus: StateStatus;
  pdfESign: string | null;
  pdfESignSummary: string | null;
  pdfESignLoadError: null;
  lastEvaluatedKey: any | null | undefined;
  attachmentPDFSaveStatus: StateStatus;
  attachmentPDFSaveError: null;
  attachmentPDFLoadStatus: StateStatus;
  attachmentPDF: string | null;
  attachmentPDFLoadError: null;
};

export type ArchiveState = {
  searchResults: ArchiveDocument[];
  searchStatus: StateStatus;
  searchError: any;
  folderFiles: Record<string, ArchiveFile[]>;
  archiveFilesStatus: StateStatus;
  archiveFilesError: any;
  downloadStatus: StateStatus;
  downloadError: any;
  orderData: Record<string, Record<string, any>>;
  orderDataStatus: StateStatus;
  orderDataError: any;
};

export type State = {
  administrator: AdministratorState;
  archive: ArchiveState;
  requestor: RequestorState;
  session: SessionState;
  signatory: SignatoryState;
};
export default State;
